import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Swords, RefreshCw, ChevronDown, ChevronUp, Flame, Target } from 'lucide-react';
import Navigation from '@/components/Navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PitcherMatchup = ({ pitcher, opponent, sideLabel, defaultOpen }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [batters, setBatters] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && batters === null && pitcher?.id) {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/mlb/matchup/${pitcher.id}/vs/${opponent.team_id}?limit=9`, { withCredentials: true });
        setBatters(res.data.batters || []);
      } catch (e) {
        console.error('matchup detail error', e);
        setBatters([]);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (defaultOpen && pitcher?.id) toggle();
  }, []);

  if (!pitcher) {
    return (
      <div className="bg-[#0a1628] rounded-md p-4 border border-[#1e3a5f]">
        <div className="text-xs text-muted-foreground uppercase tracking-wide">{sideLabel} Pitcher</div>
        <div className="text-muted-foreground mt-1">TBD</div>
      </div>
    );
  }

  const sv = pitcher.savant || {};
  const chips = [
    { label: 'Velo', value: sv.fastball_velocity, unit: '' },
    { label: 'Spin', value: sv.spin_rate, unit: '' },
    { label: 'Whiff%', value: sv.whiff_pct, unit: '%' },
    { label: 'K%', value: sv.k_pct, unit: '%' },
    { label: 'Brl% Allowed', value: sv.barrel_pct_allowed, unit: '%' },
    { label: 'HH% Allowed', value: sv.hard_hit_pct_allowed, unit: '%' },
    { label: 'xBA-a', value: sv.xba_against, unit: '' },
    { label: 'xSLG-a', value: sv.xslg_against, unit: '' },
    { label: 'xwOBA-a', value: sv.xwoba_against, unit: '' },
  ];

  return (
    <div className="bg-[#0a1628] rounded-md p-4 border border-[#1e3a5f]" data-testid={`pitcher-block-${pitcher.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide">{sideLabel} Pitcher</div>
          <button
            onClick={() => navigate(`/players?pid=${pitcher.id}&name=${encodeURIComponent(pitcher.name || '')}`)}
            className="font-semibold text-white hover:text-brand-cyan transition-colors"
            data-testid={`pitcher-link-${pitcher.id}`}
          >
            {pitcher.name}
          </button>
          <div className="text-xs text-muted-foreground">vs {opponent.abbr} lineup</div>
        </div>
        <Button size="sm" variant="outline" onClick={toggle} className="border-[#1e3a5f] text-white hover:bg-[#112240]" data-testid={`toggle-matchup-${pitcher.id}`}>
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          <span className="ml-1 hidden sm:inline">Batters</span>
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        {chips.map((c, i) => (
          <span key={i} className="text-xs px-2 py-1 rounded bg-[#0f1e36] border border-[#1e3a5f] text-brand-cyan font-mono">
            {c.label}: {c.value != null ? `${c.value}${c.unit}` : '—'}
          </span>
        ))}
      </div>

      {open && (
        <div className="mt-4">
          {loading ? (
            <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-8 skeleton rounded" />)}</div>
          ) : !batters || batters.length === 0 ? (
            <p className="text-sm text-muted-foreground">No batter data available.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground text-[10px] uppercase border-b border-[#1e3a5f]">
                    <th className="text-left py-2">Batter</th>
                    <th className="text-center py-2" title="Avg Exit Velocity">EV</th>
                    <th className="text-center py-2" title="Hard-Hit %">HH%</th>
                    <th className="text-center py-2" title="Barrel %"><Flame className="h-3 w-3 inline" /></th>
                    <th className="text-center py-2">xBA</th>
                    <th className="text-center py-2">xSLG</th>
                    <th className="text-center py-2">xwOBA</th>
                    <th className="text-center py-2" title="Career vs this pitcher">AB</th>
                    <th className="text-center py-2">H</th>
                    <th className="text-center py-2">HR</th>
                    <th className="text-center py-2">AVG</th>
                  </tr>
                </thead>
                <tbody>
                  {batters.map((b, i) => {
                    const sv2 = b.savant || {};
                    const bvp = b.bvp || {};
                    const bvpEdge = (bvp.atBats >= 4 && parseFloat(bvp.avg) >= 0.3) || bvp.homeRuns >= 2;
                    const statEdge = (sv2.xwoba >= 0.34) && (sv.xwoba_against != null && sv.xwoba_against >= 0.32);
                    const hot = bvpEdge || statEdge;
                    return (
                      <tr key={b.id} className={`border-b border-[#1e3a5f]/40 ${hot ? 'bg-brand-lime/10' : ''}`} data-testid={`bvp-row-${i}`}>
                        <td className="py-2 text-white font-medium">
                          <span className="flex items-center gap-1.5">
                            <button
                              onClick={() => navigate(`/players?pid=${b.id}&name=${encodeURIComponent(b.name || '')}`)}
                              className="hover:text-brand-cyan transition-colors"
                              data-testid={`bvp-batter-link-${i}`}
                            >
                              {b.name}
                            </button>
                            {hot && (
                              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-brand-lime/20 text-brand-lime border border-brand-lime/40 flex items-center gap-0.5">
                                <Target className="h-2.5 w-2.5" />EDGE
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="text-center font-mono text-white">{sv2.avg_exit_velocity ?? '—'}</td>
                        <td className="text-center font-mono text-white">{sv2.hard_hit_pct ?? '—'}</td>
                        <td className="text-center font-mono text-brand-cyan">{sv2.barrel_pct ?? '—'}</td>
                        <td className="text-center font-mono text-brand-cyan">{sv2.xba ?? '—'}</td>
                        <td className="text-center font-mono text-brand-cyan">{sv2.xslg ?? '—'}</td>
                        <td className="text-center font-mono text-brand-cyan">{sv2.xwoba ?? '—'}</td>
                        <td className="text-center text-muted-foreground">{bvp.atBats ?? 0}</td>
                        <td className="text-center text-white">{bvp.hits ?? 0}</td>
                        <td className={`text-center font-bold ${bvp.homeRuns > 0 ? 'text-brand-lime' : 'text-white'}`}>{bvp.homeRuns ?? 0}</td>
                        <td className="text-center font-mono text-white">{bvp.avg ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-[10px] text-muted-foreground mt-2"><Target className="h-2.5 w-2.5 inline text-brand-lime" /> EDGE = favorable batter matchup vs this pitcher</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Matchups = () => {
  const [searchParams] = useSearchParams();
  const teamParam = searchParams.get('team');
  const [games, setGames] = useState([]);
  const [date, setDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async () => {
    setRefreshing(true);
    try {
      const res = await axios.get(`${API}/mlb/matchups`, { withCredentials: true });
      setGames(res.data.games || []);
      setDate(res.data.date || '');
    } catch (e) {
      console.error('matchups error', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (!teamParam || !games.length) return;
    const g = games.find(x => x.away.abbr === teamParam || x.home.abbr === teamParam);
    if (g) {
      setTimeout(() => {
        document.getElementById(`game-${g.game_pk}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
    }
  }, [games]);

  return (
    <div className="min-h-screen bg-[#0a1628]">
      <Navigation />
      <main className="pt-20 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight flex items-center gap-3">
                <Swords className="h-8 w-8 text-brand-lime" />
                MLB Matchups
              </h1>
              <p className="text-muted-foreground mt-1">Probable pitcher vs batter matchups {date && `• ${date}`}</p>
            </div>
            <Button onClick={fetchData} variant="outline" className="border-[#1e3a5f] text-white hover:bg-[#112240]" disabled={refreshing} data-testid="refresh-matchups-btn">
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-48 skeleton rounded-lg" />)}</div>
          ) : games.length === 0 ? (
            <Card className="bg-[#0f1e36] border-[#1e3a5f]"><CardContent className="p-12 text-center text-muted-foreground">No MLB games scheduled today.</CardContent></Card>
          ) : (
            <div className="space-y-5">
              {games.map((g, idx) => (
                <Card key={g.game_pk || idx} id={`game-${g.game_pk}`} className="bg-[#0f1e36] border-[#1e3a5f]" data-testid={`matchup-game-${idx}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="font-heading text-xl font-bold text-white">
                        {g.away.abbr} <span className="text-muted-foreground text-sm">({g.away.record})</span>
                        <span className="text-muted-foreground mx-2">@</span>
                        {g.home.abbr} <span className="text-muted-foreground text-sm">({g.home.record})</span>
                      </div>
                      <Badge variant="outline" className="border-brand-cyan text-brand-cyan">{g.status}</Badge>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <PitcherMatchup pitcher={g.away.pitcher} opponent={g.home} sideLabel={`${g.away.abbr} (Away)`} defaultOpen={teamParam === g.home.abbr} />
                      <PitcherMatchup pitcher={g.home.pitcher} opponent={g.away} sideLabel={`${g.home.abbr} (Home)`} defaultOpen={teamParam === g.away.abbr} />
                    </div>
                    {g.venue && <div className="text-xs text-muted-foreground mt-3">{g.venue}</div>}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Matchups;
